import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, Subscription } from 'rxjs';
import { pageSortChange } from 'src/app/services/table';
import { EventMessage } from 'src/app/model/event-message';
import { EVENTS_DEFAULT_SORT_COLUMN, EVENTS_DEFAULT_SORT_COLUMN_DIRECTION, EVENTS_PAGE_DEFAULT_SIZE, EVENTS_PAGE_SIZE_OPTIONS } from 'src/app/conf/events';
import * as EventActions from 'src/app/store/event/event.actions';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { AppState } from 'src/app/store/app.reducer';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { Lang } from 'src/app/model/lang';
import { selectEventsLoading, selectEvents, selectEventsPaging, selectEventsPagination, selectEventsFilter } from 'src/app/store/event/event.selector';
import { EventFilterParams } from 'src/app/model/api/filter-params';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { TableLazyLoadEvent } from 'primeng/table';
import moment from 'moment';
import { DEFAULT_LANG } from 'src/app/conf/languages';

@Component({
	selector: 'app-events-table',
	templateUrl: './events-table.component.html',
	providers: [ConfirmationService, DialogService]
})

export class EventsTableComponent implements OnInit, OnDestroy {
	currentLang: Lang = DEFAULT_LANG;
	currentLang$: Observable<Lang | undefined>;
	eventsLoading$: Observable<boolean | undefined>;
	eventsLoadingSub: Subscription | undefined;
	events$: Observable<EventMessage[] | undefined>;
	eventsSub: Subscription | undefined;
	eventsPaging$: Observable<ItemsPagination | undefined>;
	eventsPagingSub: Subscription | undefined;
	eventsPagination$: Observable<PaginationParams | undefined>;
	eventsFilter$: Observable<EventFilterParams | undefined>;
	eventsQuerySub: Subscription | undefined;

	loading: boolean;

	paging: PaginationParams = {
		size: EVENTS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: EVENTS_DEFAULT_SORT_COLUMN,
		order: EVENTS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	eventMessages: EventMessage[];
	pageSizeOptions = EVENTS_PAGE_SIZE_OPTIONS;
	totalRecords: number;
	pageSize: number = EVENTS_PAGE_DEFAULT_SIZE;

	// Design
	screenWidth: number;

	MAX_MD_WIDTH_SHORT = 858;
	constructor(
		private store: Store<AppState>,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.eventsLoading$ = this.store.select(selectEventsLoading);
		this.events$ = this.store.select(selectEvents);
		this.eventsPaging$ = this.store.select(selectEventsPaging);
		this.eventsPagination$ = this.store.select(selectEventsPagination);
		this.eventsFilter$ = this.store.select(selectEventsFilter);
	}

	ngOnInit(): void {
		this.subscribeAll();
	}

	subscribeAll(): void {
		this.eventsQuerySub = combineLatest([
			this.eventsFilter$,
			this.eventsPagination$,
			this.currentLang$,
		]).pipe(
			filter((query): query is [EventFilterParams, PaginationParams, Lang] =>
				!!query[0] && !!query[1] && !!query[2]
			),
			// Filter and page are often updated together. Wait for that turn to settle.
			debounceTime(0),
			map(([eventsFilter, pagination, lang]) => ({
				filter: eventsFilter,
				pagination,
				langCode: lang.code,
				lang,
			})),
			distinctUntilChanged((previous, current) => sameEventsQuery(previous, current)),
		).subscribe(({ pagination, lang }) => {
			this.currentLang = lang;
			this.paging = pagination;
			this.pageSize = pagination.size ?? this.pageSize;
			this.store.dispatch(EventActions.getEvents({ language: lang.code }));
		});
		this.eventsSub = this.events$.subscribe(
			eventMessages => {
				this.eventMessages = eventMessages;
			}
		);
		this.eventsPagingSub = this.eventsPaging$.pipe(filter(p => !!p))
			.subscribe(
				paging => {
					this.totalRecords = paging.TotalCount;
				}
			);
		this.eventsLoadingSub = this.eventsLoading$.subscribe(loading => {
			this.loading = loading;
		});
	}

	ngOnDestroy(): void {
		this.eventsQuerySub?.unsubscribe();
		this.eventsSub?.unsubscribe();
		this.eventsPagingSub?.unsubscribe();
		this.eventsLoadingSub?.unsubscribe();
	}

	setOnPagingChanges(): void {
		/*this.pageSortSub = merge(this.sort.sortChange, this.paginator.page)
			.pipe(debounceTime(500))
			.subscribe((merged: Sort & PageEvent) => {
				const p: PaginationParams = {};
				const pagination = pageSortChange(p, merged);
				this.store.dispatch(EventActions.setPagination(pagination));
			});*/
	}

	loadEvents(event: TableLazyLoadEvent) {
		this.paging = pageSortChange(this.paging, event);
		this.pageSize = this.paging.size ?? this.pageSize;
		this.store.dispatch(EventActions.setPagination(this.paging));
	}

	get tableFirst(): number {
		const page = this.paging?.page ?? 1;
		const size = this.paging?.size ?? this.pageSize;
		return (page - 1) * size;
	}

	getEventDateLoc(eventDateLoc) {
		const currentLang = this.currentLang.code.substring(0,2);
		return moment.utc(eventDateLoc).locale(currentLang).format('D MMM YYYY, HH:mm:ss');
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}
}

function sameEventsQuery(
	previous: { filter: EventFilterParams; pagination: PaginationParams; langCode: string },
	current: { filter: EventFilterParams; pagination: PaginationParams; langCode: string },
): boolean {
	return previous.langCode === current.langCode
		&& previous.pagination.page === current.pagination.page
		&& previous.pagination.size === current.pagination.size
		&& previous.pagination.sort === current.pagination.sort
		&& previous.pagination.order === current.pagination.order
		&& previous.filter.deviceId === current.filter.deviceId
		&& previous.filter.typeId === current.filter.typeId
		&& dateTime(previous.filter.startDate) === dateTime(current.filter.startDate)
		&& dateTime(previous.filter.endDate) === dateTime(current.filter.endDate);
}

function dateTime(value?: Date): number | undefined {
	return value instanceof Date ? value.getTime() : undefined;
}
